// Admin → Email (M3, motiflink §4.5). Four surfaces behind one route because
// they are one job: templates · send log · suppressed · brand.
//
// Chrome is English-only by owner ruling (CLAUDE.md §5). The TEMPLATES
// themselves are bilingual — that's what the AR/EN switch in the editor edits.

import { Button, Dialog, DialogContent, Input, Textarea } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { DataTable, RowActions, type Column } from '~/components';
import { ApiError } from '~/lib/api';
import {
  addSuppressed,
  getEmailSettings,
  getEmailTemplate,
  getVariableHints,
  listEmailLog,
  listEmailTemplates,
  listSuppressed,
  removeSuppressed,
  sendTestEmail,
  updateEmailSettings,
  updateEmailTemplate,
  type EmailTemplateSummary,
  type TestSendResult,
} from '~/lib/email';

type Tab = 'templates' | 'log' | 'suppressed' | 'brand';

export interface Search {
  tab?: Tab;
  slug?: string;
  lang?: string;
}

export const Route = createFileRoute('/_authed/email')({
  validateSearch: (search: Record<string, unknown>): Search => ({
    tab: isTab(search.tab) ? search.tab : undefined,
    slug: typeof search.slug === 'string' && search.slug ? search.slug : undefined,
    lang: search.lang === 'ar' ? 'ar' : search.lang === 'en' ? 'en' : undefined,
  }),
  component: EmailPage,
});

function isTab(v: unknown): v is Tab {
  return v === 'templates' || v === 'log' || v === 'suppressed' || v === 'brand';
}

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'templates', label: 'Templates' },
  { id: 'log', label: 'Send log' },
  { id: 'suppressed', label: 'Suppressed' },
  { id: 'brand', label: 'Brand' },
];

function EmailPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { tab = 'templates' } = Route.useSearch();
  const [error, setError] = useState<string | null>(null);

  const onError = (e: unknown) =>
    setError(e instanceof ApiError ? e.message : 'Something went wrong.');

  return (
    <div className="mx-auto max-w-6xl">
      <header>
        <h1 className="text-2xl font-semibold text-admin-900">Email</h1>
        <p className="mt-1 text-sm text-ink-700">
          Templates are stored in the database — edits apply to the next send, no deploy needed.
        </p>
      </header>

      {error && (
        <p role="alert" className="mt-4 rounded-xl bg-heart/10 px-4 py-3 text-sm text-heart">
          {error}
        </p>
      )}

      <div role="tablist" aria-label="Email sections" className="mt-6 flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => void navigate({ search: { tab: t.id }, replace: true })}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
              tab === t.id ? 'bg-brand-500 text-white' : 'text-admin-700 hover:bg-admin-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === 'templates' && <TemplatesTab onError={onError} />}
        {tab === 'log' && <LogTab />}
        {tab === 'suppressed' && <SuppressedTab onError={onError} />}
        {tab === 'brand' && <BrandTab onError={onError} />}
      </div>
    </div>
  );
}

// ─── templates ──────────────────────────────────────────────────────────────

function TemplatesTab({ onError }: { onError: (e: unknown) => void }) {
  const [editing, setEditing] = useState<{ slug: string; lang: string } | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ['email', 'templates'],
    queryFn: ({ signal }) => listEmailTemplates(undefined, signal),
    staleTime: 30_000,
  });

  // Group the flat (slug, lang) rows into one row per slug with both languages,
  // which is how an editor thinks about a template.
  const bySlug = new Map<string, { name: string; langs: EmailTemplateSummary[] }>();
  for (const row of templates.data ?? []) {
    const entry = bySlug.get(row.slug) ?? { name: row.name, langs: [] };
    entry.langs.push(row);
    bySlug.set(row.slug, entry);
  }

  type TemplateRow = { slug: string; name: string; langs: EmailTemplateSummary[] };

  const templateColumns: Column<TemplateRow>[] = [
    {
      key: 'slug',
      header: 'Slug',
      alwaysVisible: true,
      render: (r) => <span className="font-mono text-xs text-admin-900">{r.slug}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      render: (r) => <span className="font-medium text-admin-900">{r.name}</span>,
    },
    {
      key: 'langs',
      header: 'Languages',
      render: (r) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {r.langs.map((l) => (
            <button
              key={l.languageCode}
              type="button"
              onClick={() => setEditing({ slug: r.slug, lang: l.languageCode })}
              className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${
                l.isActive
                  ? 'bg-success-50 text-success-700 hover:bg-success-500/20'
                  : 'bg-admin-100 text-admin-500 hover:bg-admin-200'
              }`}
              title={l.isActive ? 'Active — click to edit' : 'Inactive — click to edit'}
            >
              {l.languageCode.toUpperCase()}
            </button>
          ))}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      alwaysVisible: true,
      width: '5.5rem',
      render: (r) => (
        <RowActions
          label={`template ${r.slug}`}
          primary={{
            id: 'edit',
            label: 'Edit',
            icon: 'edit',
            // Templates are per-language rows; editing opens the FIRST
            // language, and the pills above jump straight to a specific one.
            onSelect: () => setEditing({ slug: r.slug, lang: r.langs[0]?.languageCode ?? 'ar' }),
          }}
          actions={[
            {
              id: 'test',
              label: 'Send test email',
              icon: 'envelope',
              onSelect: () => setTesting(r.slug),
            },
            {
              id: 'copy-slug',
              label: 'Copy slug',
              icon: 'copy',
              onSelect: () => void navigator.clipboard?.writeText(r.slug),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={templateColumns}
        rows={[...bySlug.entries()].map(([slug, entry]) => ({ slug, ...entry }))}
        rowKey={(r) => r.slug}
        loading={templates.isLoading}
        error={templates.error}
        onRetry={() => void templates.refetch()}
        caption="Email templates"
        skeletonRows={5}
        empty={{
          icon: 'envelope',
          title: 'No templates yet',
          description: 'Run the email-templates seed to create them.',
        }}
      />

      {editing && (
        <TemplateEditor
          slug={editing.slug}
          lang={editing.lang}
          onClose={() => setEditing(null)}
          onError={onError}
        />
      )}
      {testing && (
        <TestSendDialog slug={testing} onClose={() => setTesting(null)} onError={onError} />
      )}
    </>
  );
}

function TemplateEditor({
  slug,
  lang,
  onClose,
  onError,
}: {
  slug: string;
  lang: string;
  onClose: () => void;
  onError: (e: unknown) => void;
}) {
  const qc = useQueryClient();
  const template = useQuery({
    queryKey: ['email', 'template', slug, lang],
    queryFn: ({ signal }) => getEmailTemplate(slug, lang, signal),
  });
  const hints = useQuery({
    queryKey: ['email', 'hints'],
    queryFn: ({ signal }) => getVariableHints(signal),
    staleTime: 300_000,
  });

  const [subject, setSubject] = useState<string | null>(null);
  const [body, setBody] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      updateEmailTemplate(slug, lang, {
        subject: subject ?? template.data!.subject,
        htmlBody: body ?? template.data!.htmlBody,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['email'] });
      onClose();
    },
    onError,
  });

  const rtl = lang === 'ar';
  const vars = hints.data?.[slug] ?? [];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={`${slug} · ${lang.toUpperCase()}`} className="max-w-3xl bg-white">
        {template.isLoading ? (
          <p className="text-sm text-ink-500">Loading…</p>
        ) : !template.data ? (
          <p className="text-sm text-heart">Template not found.</p>
        ) : (
          <div className="space-y-4">
            {vars.length > 0 && (
              <div className="rounded-xl bg-admin-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-admin-400">
                  Available variables
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {vars.map((v) => (
                    <code
                      key={v}
                      className="rounded-md bg-white px-1.5 py-0.5 font-mono text-[11px] text-admin-700 ring-1 ring-admin-200"
                    >{`{{${v}}}`}</code>
                  ))}
                </div>
                <p className="mt-1.5 text-xs text-ink-500">
                  An unknown variable renders literally (e.g. <code>{'{{oops}}'}</code>) rather than
                  blank, so mistakes are visible in a test send.
                </p>
              </div>
            )}

            <label className="block text-sm font-medium text-ink-700">
              Subject
              <Input
                dir={rtl ? 'rtl' : 'ltr'}
                value={subject ?? template.data.subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1"
              />
            </label>

            <label className="block text-sm font-medium text-ink-700">
              Body (inner HTML)
              <Textarea
                dir={rtl ? 'rtl' : 'ltr'}
                rows={14}
                value={body ?? template.data.htmlBody}
                onChange={(e) => setBody(e.target.value)}
                className="mt-1 font-mono text-xs"
              />
              <span className="mt-1 block text-xs text-ink-500">
                Inner HTML only — the branded header, footer and {rtl ? 'RTL direction' : 'layout'}{' '}
                are added at send time.
              </span>
            </label>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save template'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TestSendDialog({
  slug,
  onClose,
  onError,
}: {
  slug: string;
  onClose: () => void;
  onError: (e: unknown) => void;
}) {
  const [to, setTo] = useState('');
  const [lang, setLang] = useState('en');
  const [result, setResult] = useState<TestSendResult | null>(null);

  const send = useMutation({
    mutationFn: () => sendTestEmail({ templateSlug: slug, to, lang }),
    onSuccess: setResult,
    onError,
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={`Send test · ${slug}`} className="max-w-md bg-white">
        <p className="text-sm text-ink-700">
          Sends the template <strong>raw</strong> — no variable substitution and no suppression
          check, so you see exactly what was authored.
        </p>

        <label className="mt-4 block text-sm font-medium text-ink-700">
          To
          <Input
            type="email"
            autoFocus
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="you@example.com"
            className="mt-1"
          />
        </label>

        <label className="mt-3 block text-sm font-medium text-ink-700">
          Language
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="mt-1 block h-10 w-full rounded-2xl border border-burgundy-500/15 bg-cream-50 px-3 text-sm text-ink-900"
          >
            <option value="en">English</option>
            <option value="ar">العربية</option>
          </select>
        </label>

        {result && (
          <div
            className={`mt-4 rounded-xl px-3 py-2 text-sm ${
              result.ok ? 'bg-leaf/10 text-leaf' : 'bg-heart/10 text-heart'
            }`}
          >
            <p className="font-semibold">
              {result.ok ? 'Sent' : 'Failed'} via {result.transport} · {result.durationMs}ms
            </p>
            {result.transport === 'console' && result.ok && (
              <p className="mt-1 text-xs text-ink-700">
                No BREVO_API_KEY is set, so this was logged by the Worker rather than delivered. The
                full pipeline still ran.
              </p>
            )}
            {result.error && <p className="mt-1 text-xs">{result.error}</p>}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button disabled={!to.includes('@') || send.isPending} onClick={() => send.mutate()}>
            {send.isPending ? 'Sending…' : 'Send test'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── send log ───────────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, string> = {
  sent: 'bg-leaf/15 text-leaf',
  failed: 'bg-heart/10 text-heart',
  suppressed: 'bg-sun/15 text-sun-600',
};

function LogTab() {
  const [status, setStatus] = useState('');
  const [email, setEmail] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const log = useQuery({
    queryKey: ['email', 'log', status, email, offset],
    queryFn: ({ signal }) =>
      listEmailLog(
        { status: status || undefined, email: email || undefined, limit: LIMIT, offset },
        signal,
      ),
    staleTime: 5_000,
  });

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border border-burgundy-500/10 bg-white p-4">
        <label className="text-sm font-medium text-ink-700">
          Status
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
            }}
            className="mt-1 block h-10 rounded-2xl border border-burgundy-500/15 bg-cream-50 px-3 text-sm text-ink-900"
          >
            <option value="">All</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="suppressed">Suppressed</option>
          </select>
        </label>
        <label className="flex-1 text-sm font-medium text-ink-700">
          Recipient
          <Input
            type="search"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setOffset(0);
            }}
            placeholder="parent@example.com"
            className="mt-1 h-10 min-w-48"
          />
        </label>
      </div>

      <div className="overflow-hidden rounded-2xl border border-burgundy-500/10 bg-white">
        {log.isLoading ? (
          <p className="p-6 text-sm text-ink-500">Loading…</p>
        ) : (log.data?.rows.length ?? 0) === 0 ? (
          <p className="p-6 text-sm text-ink-500">No sends recorded yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-burgundy-500/10 bg-admin-50">
              <tr className="text-xs font-semibold uppercase tracking-wider text-admin-400">
                <th className="px-4 py-3 text-start">When</th>
                <th className="px-4 py-3 text-start">Template</th>
                <th className="px-4 py-3 text-start">Recipient</th>
                <th className="px-4 py-3 text-start">Status</th>
              </tr>
            </thead>
            <tbody>
              {log.data!.rows.map((row) => (
                <tr key={row.id} className="border-b border-burgundy-500/5 last:border-0">
                  <td className="px-4 py-3 text-xs text-ink-500">
                    {new Date(row.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-admin-900">
                    {row.templateSlug}
                    {(row.metadata as { test?: boolean })?.test && (
                      <span className="ms-1.5 rounded-full bg-admin-100 px-1.5 py-0.5 text-[10px] font-semibold text-admin-500">
                        test
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-admin-900">{row.recipientEmail}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        STATUS_STYLE[row.status] ?? 'bg-admin-100 text-admin-500'
                      }`}
                      title={row.errorMessage ?? undefined}
                    >
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(log.data?.total ?? 0) > LIMIT && (
        <div className="mt-4 flex items-center justify-between text-sm text-ink-500">
          <span>
            {offset + 1}–{Math.min(offset + LIMIT, log.data!.total)} of {log.data!.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={offset + LIMIT >= (log.data?.total ?? 0)}
              onClick={() => setOffset(offset + LIMIT)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── suppressed ─────────────────────────────────────────────────────────────

const REASON_HELP: Record<string, string> = {
  bounced: 'The provider reported a hard bounce — the address does not exist.',
  complaint: 'Marked as spam by the recipient.',
  unsubscribed: 'The recipient used the unsubscribe link.',
  manual: 'Added by a staff member.',
};

function SuppressedTab({ onError }: { onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const list = useQuery({
    queryKey: ['email', 'suppressed', q],
    queryFn: ({ signal }) => listSuppressed({ q: q || undefined, limit: 100 }, signal),
    staleTime: 10_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['email', 'suppressed'] });
  const add = useMutation({
    mutationFn: () => addSuppressed(newEmail.trim()),
    onSuccess: () => {
      setNewEmail('');
      void invalidate();
    },
    onError,
  });
  const remove = useMutation({ mutationFn: removeSuppressed, onSuccess: invalidate, onError });

  return (
    <>
      <p className="mb-4 text-sm text-ink-700">
        Suppressed addresses are skipped by every send. Bounces and complaints land here
        automatically via the provider webhook — removing one re-enables delivery, so only do it if
        you know the address is good again.
      </p>

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border border-burgundy-500/10 bg-white p-4">
        <label className="flex-1 text-sm font-medium text-ink-700">
          Search
          <Input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by address"
            className="mt-1 h-10 min-w-48"
          />
        </label>
        <label className="flex-1 text-sm font-medium text-ink-700">
          Add address
          <Input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="blocked@example.com"
            className="mt-1 h-10 min-w-48"
          />
        </label>
        <Button disabled={!newEmail.includes('@') || add.isPending} onClick={() => add.mutate()}>
          {add.isPending ? 'Adding…' : 'Suppress'}
        </Button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-burgundy-500/10 bg-white">
        {list.isLoading ? (
          <p className="p-6 text-sm text-ink-500">Loading…</p>
        ) : (list.data?.rows.length ?? 0) === 0 ? (
          <p className="p-6 text-sm text-ink-500">Nothing suppressed — that&rsquo;s a good sign.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-burgundy-500/10 bg-admin-50">
              <tr className="text-xs font-semibold uppercase tracking-wider text-admin-400">
                <th className="px-4 py-3 text-start">Address</th>
                <th className="px-4 py-3 text-start">Reason</th>
                <th className="px-4 py-3 text-start">Since</th>
                <th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data!.rows.map((row) => (
                <tr key={row.id} className="border-b border-burgundy-500/5 last:border-0">
                  <td className="px-4 py-3 text-admin-900">{row.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className="rounded-full bg-admin-100 px-2 py-0.5 text-[11px] font-semibold text-admin-600"
                      title={REASON_HELP[row.reason]}
                    >
                      {row.reason}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-500">
                    {new Date(row.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(row.email)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ─── brand hub ──────────────────────────────────────────────────────────────

function BrandTab({ onError }: { onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ['email', 'settings'],
    queryFn: ({ signal }) => getEmailSettings(signal),
  });

  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const value = (key: string): string => {
    const stored = (settings.data as Record<string, unknown> | undefined)?.[key];
    // Email settings are text fields; anything non-scalar would render as
    // "[object Object]" in the input, so treat it as empty instead.
    const fallback =
      stored === null || stored === undefined || typeof stored === 'object'
        ? ''
        : // eslint-disable-next-line @typescript-eslint/no-base-to-string
          String(stored);
    return draft?.[key] ?? fallback;
  };

  const save = useMutation({
    mutationFn: () =>
      updateEmailSettings({
        companyName: value('companyName'),
        tagline: value('tagline') || null,
        websiteUrl: value('websiteUrl') || null,
        contactEmail: value('contactEmail') || null,
        footerText: value('footerText') || null,
        logoUrl: value('logoUrl') || null,
      }),
    onSuccess: async () => {
      setDraft(null);
      await qc.invalidateQueries({ queryKey: ['email', 'settings'] });
    },
    onError,
  });

  if (settings.isLoading) return <p className="text-sm text-ink-500">Loading…</p>;

  const FIELDS: Array<{ key: string; label: string; hint?: string; type?: string }> = [
    { key: 'companyName', label: 'Company name' },
    { key: 'tagline', label: 'Tagline' },
    { key: 'websiteUrl', label: 'Website URL', type: 'url' },
    { key: 'contactEmail', label: 'Contact email', type: 'email' },
    {
      key: 'logoUrl',
      label: 'Logo URL',
      hint: 'Shown above the card in every email.',
      type: 'url',
    },
    { key: 'footerText', label: 'Footer text', hint: 'Appears under the copyright line.' },
  ];

  return (
    <div className="max-w-xl rounded-2xl border border-burgundy-500/10 bg-white p-5">
      <p className="mb-4 text-sm text-ink-700">
        These values appear in the shell wrapped around every outgoing email, in both languages.
      </p>
      <div className="space-y-4">
        {FIELDS.map((f) => (
          <label key={f.key} className="block text-sm font-medium text-ink-700">
            {f.label}
            <Input
              type={f.type ?? 'text'}
              value={value(f.key)}
              onChange={(e) => setDraft({ ...(draft ?? {}), [f.key]: e.target.value })}
              className="mt-1"
            />
            {f.hint && <span className="mt-1 block text-xs text-ink-500">{f.hint}</span>}
          </label>
        ))}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" disabled={!draft} onClick={() => setDraft(null)}>
          Reset
        </Button>
        <Button disabled={!draft || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save brand settings'}
        </Button>
      </div>
    </div>
  );
}
